import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { PaginationService } from '@services/pagination/pagination.service';
import { ProductPackagesDataServiceProvider } from '@services/settings/product-packages.data.service';
import { MessageService } from '@services/helpers/message-service/message.service';
import { DecimalPipe } from '@angular/common';
import { ProductName } from '@models/models';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap/modal/modal-ref';
import { SNModalService } from '@web-services/modal/snModal.service';
import { DeleteItemModalComponent } from '@web-components/settings/common/delete-item-modal/delete-item-modal';

@Component({
	selector: 'product-block',
	template: require('./product-block.component.html')
})
export class ProductBlockComponent implements OnInit, OnDestroy {
	@Input() companyId: number;
	modalTitle: string;
	updateProductModalId = 'updateProductModal';
	pageRequest: any;
	products: ProductName[] = [];
	itemsQuantity: number;
	pageNumbers: any;
	disabledBtnModal = false;
	disabledBtnDeleteModal = false;
	private allowedFilters: string[];

	readonly MODAL_TITLES = [
		'Add New Product',
		'Duplicate Product',
		'Edit Product'
	];

	readonly PRODUCTS_REQUEST_DEFAULT = {
		filterBy: 'add_ons_and_gifts',
		paging: {
			pageNumber: 0,
			pageSize: 10,
			searchToken: null
		},
		sortCriterias: [
			{
				fieldName: 'name',
				sortAscending: true
			}
		]
	};

	readonly PRODUCT_DEFAULT = {
		name: '',
		taxType: 'INCLUDED',
		type: 'ADD_ON',
		description: ''
	};

	selectedPackage = this.PRODUCT_DEFAULT;
	deletedPackage = this.PRODUCT_DEFAULT;
	deleteModalRef: NgbModalRef;

	constructor(
		private productPackagesService: ProductPackagesDataServiceProvider,
		private messageService: MessageService,
		private paginationService: PaginationService,
		private decimalPipe: DecimalPipe,
		private modalService: SNModalService
	) {}

	ngOnInit() {
		this.applyPageProductFromLocalStorage();
		this.getProductsData();
		this.productPackagesService.updateProductPackageSubject
			.debounceTime(500)
			.subscribe(() => {
				this.pageRequest.paging.searchToken = null;
				this.pageRequest.paging.pageNumber = 0;
				this.getProductsData();
			});
	}

	ngOnDestroy() {
		this.productPackagesService.updateProductPackageSubject.complete();
	}

	deleteProductSubscription() {
		this.modalService
			.getModalData()
			.take(1)
			.subscribe(data => this.deleteProduct(data));
	}

	private getProductsData(): void {
		this.productPackagesService
			.getPackagesOrProducts(this.pageRequest)
			.subscribe(
				data => this.successGetProductsData(data),
				error => this.messageService.showDefaultErrorMessage(error)
			);
	}

	private successGetProductsData(data): void {
		this.products = JSON.parse(JSON.stringify(data.items));
		this.itemsQuantity = data ? data.totalSize : 0;
		this.pageNumbers = this.paginationService.calculatePageNumbers(
			this.itemsQuantity,
			this.pageRequest.paging.pageSize
		);
		this.pageRequest.paging.searchToken = data.searchToken;
		this.allowedFilters = data.sortingFields;
		this.setPageProductToLocalStorage(this.pageRequest);
	}

	private findById(id): any {
		const packageItem = this.products.filter(el => el.id === id);

		return JSON.parse(JSON.stringify(packageItem[0]));
	}

	isFilterAllowed(filterName: string) {
		if (!this.allowedFilters) {
			return false;
		}

		return this.allowedFilters.includes(filterName);
	}

	orderByField(filter: string): void {
		if (this.pageRequest.sortCriterias[0].fieldName === filter) {
			this.pageRequest.sortCriterias[0].sortAscending = !this.pageRequest
				.sortCriterias[0].sortAscending;
		} else {
			this.pageRequest.sortCriterias[0].fieldName = filter;
			this.pageRequest.sortCriterias[0].sortAscending = false;
		}
		this.pageRequest.paging.pageNumber = 0;

		this.getProductsData();
	}

	openEditProductModal(id: number): void {
		this.selectedPackage = this.findById(id);
		this.modalTitle = this.MODAL_TITLES[2];
		$(`#${this.updateProductModalId}`).modal('show');
	}

	openDuplicateProductModal(id: number) {
		this.selectedPackage = this.findById(id);
		this.modalTitle = this.MODAL_TITLES[1];
		$(`#${this.updateProductModalId}`).modal('show');
	}

	openAddNewProductModal(): void {
		this.selectedPackage = JSON.parse(JSON.stringify(this.PRODUCT_DEFAULT));
		this.modalTitle = this.MODAL_TITLES[0];
		$(`#${this.updateProductModalId}`).modal('show');
	}

	openDeleteProductModal(id: number) {
		this.deletedPackage = this.findById(id);

		this.deleteModalRef = this.modalService.openModal(DeleteItemModalComponent);

		this.deleteProductSubscription();

		this.deleteModalRef.componentInstance.modalTitle = 'Product';
		this.deleteModalRef.componentInstance.btnDisabled = false;
		this.deleteModalRef.componentInstance.modalData = this.deletedPackage;
		this.deleteModalRef.componentInstance.itemName = this.deletedPackage.name;
	}

	updateProductData(pageRequest) {
		this.pageRequest = pageRequest;
		this.getProductsData();
	}

	getTaxLabel(taxType: string): string {
		let taxLabel = '';
		switch (taxType) {
			case 'INCLUDED':
				taxLabel = 'Includes';
				break;
			case 'EXCLUDED':
				taxLabel = 'Excludes';
				break;
			case 'NO_TAX':
				taxLabel = 'No';
				break;
			default:
				return '';
		}
		return taxLabel;
	}

	getModalData(modalData): void {
		this.disabledBtnModal = true;
		const { eventType, data } = modalData;
		const productDataToSend = JSON.parse(JSON.stringify(data));

		switch (eventType) {
			case 'Add New Product':
				this.addNewProduct(productDataToSend);
				break;
			case 'Duplicate Product':
				this.duplicateProduct(productDataToSend);
				break;
			case 'Edit Product':
				this.updateProduct(productDataToSend);
				break;
			default:
				break;
		}
	}

	addNewProduct(dataToSend): void {
		this.productPackagesService
			.createPackagesOrProducts(dataToSend)
			.finally(() => (this.disabledBtnModal = false))
			.subscribe(
				(data: any) => {
					this.messageService.showToaster('s_4', 'productsPackages', data.name);

					this.pageRequest.paging.searchToken = null;
					this.pageRequest.paging.pageNumber = 0;
					this.getProductsData();
					$(`#${this.updateProductModalId}`).modal('hide');
				},
				error => this.messageService.showDefaultErrorMessage(error)
			);
	}

	duplicateProduct(dataToSend): void {
		this.productPackagesService
			.createPackagesOrProducts(dataToSend)
			.finally(() => (this.disabledBtnModal = false))
			.subscribe(
				(data: any) => {
					this.messageService.showToaster('s_6', 'productsPackages', data.name);
					this.pageRequest.paging.searchToken = null;
					this.pageRequest.paging.pageNumber = 0;
					this.getProductsData();
					$(`#${this.updateProductModalId}`).modal('hide');
				},
				error => this.messageService.showDefaultErrorMessage(error)
			);
	}

	convertProductToPackage(product): void {
		const body = {
			productTemplateId: product.id,
			requiredProductType: 'PACKAGE'
		};
		this.productPackagesService.convertProductPackage(body).subscribe(
			() => {
				this.productPackagesService.updateProductPackageSubject.next(
					'updatePackages'
				);
				this.messageService.showToaster(
					's_9',
					'productsPackages',
					product.name
				);
			},
			error => this.messageService.showDefaultErrorMessage(error)
		);
	}

	updateProduct(dataToSend): void {
		this.productPackagesService
			.updatePackagesOrProducts(dataToSend.id, dataToSend)
			.finally(() => (this.disabledBtnModal = false))
			.subscribe(
				(data: any) => {
					this.messageService.showToaster('s_7', 'productsPackages', data.name);
					this.pageRequest.paging.searchToken = null;
					this.pageRequest.paging.pageNumber = 0;
					this.getProductsData();
					$(`#${this.updateProductModalId}`).modal('hide');
				},
				error => this.messageService.showDefaultErrorMessage(error)
			);
	}

	deleteProduct(dataToSend): void {
		this.deleteModalRef.componentInstance.btnDisabled = true;
		this.productPackagesService
			.deletePackagesOrProducts(dataToSend.id)
			.finally(() => (this.disabledBtnDeleteModal = false))
			.subscribe(
				() => {
					this.messageService.showToaster(
						's_5',
						'productsPackages',
						dataToSend.name
					);
					this.pageRequest.paging.searchToken = null;
					this.pageRequest.paging.pageNumber = 0;
					this.getProductsData();
					this.deleteModalRef.close();
				},
				error => this.messageService.showDefaultErrorMessage(error)
			);
	}

	// LOCAL STORAGE
	private applyPageProductFromLocalStorage(): void {
		const pageRequestObject = JSON.parse(
			localStorage.getItem(`settingsProductRequest_${this.companyId}`)
		);
		this.pageRequest = this.PRODUCTS_REQUEST_DEFAULT;
		if (
			pageRequestObject &&
			pageRequestObject !== null &&
			pageRequestObject !== ''
		) {
			const { pageSize, fieldName, sortAscending } = pageRequestObject;
			this.pageRequest.paging.pageSize = pageSize;
			this.pageRequest.sortCriterias[0].fieldName = fieldName;
			this.pageRequest.sortCriterias[0].sortAscending = sortAscending;
		}
	}

	private setPageProductToLocalStorage(pageRequest): void {
		const pageRequestObject = {
			pageSize: pageRequest.paging.pageSize,
			fieldName: pageRequest.sortCriterias[0].fieldName,
			sortAscending: pageRequest.sortCriterias[0].sortAscending
		};

		const pageRequestString = JSON.stringify(pageRequestObject);

		localStorage.removeItem(`settingsProductRequest_${this.companyId}`);
		localStorage.setItem(
			`settingsProductRequest_${this.companyId}`,
			pageRequestString
		);
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=nfw9yna8xro!./web/modern/components/packages-product/product-block/product-block.component.ts